import { get, isEmpty, isNil } from 'lodash';

import { YardTaskCheckEntity, YardTaskCheckLocationOutcome } from '@nova/core';

import { isCheckTask, isMoveTask } from './yardTaskPredicates';
import {
  mapChecklist,
  mapInventoryGroups,
  mapRelatedEntity,
  transformDueDatePayloadToState
} from './yardTaskMapping';
import { mapTags } from './yardTaskTags';
import { mapDueDateTime } from './yardTaskFormatting';

import type {
  BaseTaskCreateData,
  BaseTaskViewData,
  CheckTaskApiRequestData,
  CheckTaskApiResponseData,
  CheckTaskCheckAssetContainerView,
  CheckTaskFormData,
  CheckTaskViewData,
  Maybe,
  MoveTaskApiRequestData,
  MoveTaskApiResponseData,
  MoveTaskFormData,
  MoveTaskViewData,
  Nullable,
  TaskApiResponseData,
  TaskViewData,
  YardTaskAttachment,
  YardTaskEventData,
  YardTaskInventoryGroupInput,
  YardTaskUser
} from '@nova/core';

/**
 * Maps the task detail attachments payload from `documents` endpoint to view data.
 * Shared by every task type — the detail endpoint joins them for `YardTask` regardless
 * of the child type.
 * @param apiResponse - API response data (check or move)
 * @returns Attachment documents for view data, or undefined when the payload omits them
 */
function mapTaskAttachments(apiResponse: TaskApiResponseData): YardTaskAttachment[] | undefined {
  const attachments = get(apiResponse, 'documents');

  if (!Array.isArray(attachments)) {
    return undefined;
  }

  return attachments.map((attachment: YardTaskAttachment) => ({
    id: attachment.id,
    name: attachment.name,
    mimetype: attachment.mimetype,
    url: attachment.url,
    createDateTime: attachment.createDateTime,
    uploadedBy: attachment.uploadedBy
  }));
}

/**
 * Extracts common base properties from API response to view data
 * @param apiResponse - API response data (check or move)
 * @returns Common base properties for view data
 */
function extractBaseViewDataFromApiResponse(apiResponse: TaskApiResponseData): BaseTaskViewData & {
  id: Nullable<string>;
  status: string;
  order: Nullable<number>;
  refNum: Nullable<number>;
  events: YardTaskEventData[];
} {
  return {
    id: get(apiResponse, 'id') || null,
    status: get(apiResponse, 'status') || '',
    description: get(apiResponse, 'description') || '',
    order: get(apiResponse, 'order') || null,
    refNum: get(apiResponse, 'refNum') || null,
    createdAt: get(apiResponse, 'createDateTime') || null,
    createdBy: get(apiResponse, 'createdBy') || null,
    tags: get(apiResponse, 'tags') || [],
    events: get(apiResponse, 'events') || [],
    attachments: mapTaskAttachments(apiResponse),

    assignedTo: get(apiResponse, 'assignedTo') || null,
    assignedBy: get(apiResponse, 'assignedBy') || null,
    assignedDateTime: get(apiResponse, 'assignedDateTime') || null,
    dueDateTime: get(apiResponse, 'dueDateTime') || null,
    lastChangedBy: get(apiResponse, 'lastChangedBy') || null,
    lastChangedDateTime: get(apiResponse, 'lastChangedDateTime') || null
  };
}

/**
 * Extracts common base properties from view data to form data
 * @param viewData - View data (check or move)
 * @param warehouseTimezone - Warehouse timezone
 * @returns Common base properties for form data
 */
function extractBaseFormDataFromViewData(
  viewData: TaskViewData,
  warehouseTimezone: string
): Pick<BaseTaskCreateData, 'description' | 'assignee' | 'dueByDate' | 'dueByTime' | 'tags'> & {
  id?: Maybe<string>;
  order?: Maybe<number>;
  refNum?: Maybe<number>;
  createdAt?: string;
  createdBy?: YardTaskUser;
  status?: string;
} {
  const dueDateState = viewData.dueDateTime
    ? transformDueDatePayloadToState(viewData.dueDateTime, warehouseTimezone)
    : { dueByDate: '', dueByTime: { time: '', amPm: 'pm' } };

  return {
    id: viewData.id,
    order: viewData.order,
    refNum: viewData.refNum,
    description: viewData.description,
    assignee: viewData.assignedTo?.id || null,
    dueByDate: dueDateState.dueByDate || undefined,
    dueByTime: dueDateState.dueByTime || undefined,
    tags: viewData.tags,
    createdAt: viewData.createdAt || undefined,
    createdBy: viewData.createdBy || undefined,
    status: viewData.status
  };
}

/**
 * Transforms check task API response to view data format (for presentation)
 * @param apiResponse - API response data
 * @returns Check task view data
 */
export function transformCheckTaskApiResponseToViewData(
  apiResponse: CheckTaskApiResponseData
): CheckTaskViewData {
  const relatedEntity = get(apiResponse, 'child.relatedEntity');
  const relatedId = get(apiResponse, 'child.relatedId');
  const relatedObject = get(apiResponse, 'child.relatedObject');
  const baseData = extractBaseViewDataFromApiResponse(apiResponse);
  const rawContainers = get(apiResponse, 'child.checkAssetContainers');
  const checkAssetContainers: CheckTaskCheckAssetContainerView[] | undefined = Array.isArray(
    rawContainers
  )
    ? rawContainers
        .filter(
          (
            row
          ): row is {
            outcome: YardTaskCheckLocationOutcome;
            createDateTime?: unknown;
            assetContainerId?: unknown;
          } => {
            if (!row || typeof row !== 'object' || Array.isArray(row)) {
              return false;
            }
            const o = (row as { outcome?: unknown }).outcome;
            return (
              !isNil(o) &&
              (
                Object.values(YardTaskCheckLocationOutcome) as YardTaskCheckLocationOutcome[]
              ).includes(o as YardTaskCheckLocationOutcome)
            );
          }
        )
        .map(row => ({
          outcome: row.outcome,
          // Defensive coercion: downstream code (`getCheckOutcomeChipDisplay`,
          // `pickLatestCheckOutcome`) calls `Date.parse` on this and passes the asset id
          // through `Map.get` — both would silently misbehave if the API ever returned a
          // non-string. Anything that isn't a string collapses to `null`.
          createDateTime: typeof row.createDateTime === 'string' ? row.createDateTime : null,
          assetContainerId: typeof row.assetContainerId === 'string' ? row.assetContainerId : null
        }))
    : undefined;

  const rawInventoryGroups = get(apiResponse, 'child.inventoryGroups');
  const inventoryGroups = Array.isArray(rawInventoryGroups)
    ? mapInventoryGroups(rawInventoryGroups as YardTaskInventoryGroupInput[])
    : undefined;

  return {
    ...baseData,
    type: 'check',
    category: get(apiResponse, 'child.category') || '',
    subject: relatedEntity || '',
    checklist: (get(apiResponse, 'child.checklist') || []).map((item: any) => ({
      description: item.description,
      complete: item.complete
    })),
    relatedEntity: relatedEntity || undefined,
    relatedId: relatedId || null,
    relatedObject: relatedObject || null,
    checkId: get(apiResponse, 'child.id') || undefined,
    warehouseId: get(apiResponse, 'warehouseId') || undefined,
    checkAssetContainers,
    inventoryGroups,
    events: baseData.events || []
  };
}

/**
 * Transforms move task API response to view data format (for presentation)
 * @param apiResponse - API response data
 * @returns Move task view data
 */
export function transformMoveTaskApiResponseToViewData(
  apiResponse: MoveTaskApiResponseData
): MoveTaskViewData {
  const baseData = extractBaseViewDataFromApiResponse(apiResponse);

  return {
    ...baseData,
    type: 'move',
    originSpot: get(apiResponse, 'child.originSpot') || null,
    destinationSpot: get(apiResponse, 'child.destinationSpot') || null,
    assetContainer: get(apiResponse, 'child.assetContainer') || null,
    originSpotArea: get(apiResponse, 'child.originSpotArea') || null,
    destinationSpotArea: get(apiResponse, 'child.destinationSpotArea') || null,
    // Faithful to the payload: an absent field (a payload from before the column existed) stays
    // undefined here rather than being read as `true`. Callers that need a concrete boolean for a
    // checkbox default it themselves, so "never set" and "deliberately set to true" don't collapse
    // into the same value the moment this round-trips through one save.
    reserveDestinationSpot: get(apiResponse, 'child.reserveDestinationSpot'),
    events: baseData.events || []
  };
}

/**
 * Transforms task API response to view data format (for presentation)
 * @param apiResponse - API response data
 * @param warehouseTimezone - Warehouse timezone
 * @returns View data object (CheckTaskViewData or MoveTaskViewData)
 */
export function transformTaskApiResponseToViewData(
  apiResponse: Maybe<TaskApiResponseData>
): Nullable<TaskViewData> {
  if (!apiResponse) {
    return null;
  }

  if (isCheckTask(apiResponse)) {
    return transformCheckTaskApiResponseToViewData(apiResponse);
  }

  if (isMoveTask(apiResponse)) {
    return transformMoveTaskApiResponseToViewData(apiResponse);
  }

  return null;
}

/**
 * Transforms check task view data to form data format (for forms)
 * @param viewData - View data
 * @param warehouseTimezone - Warehouse timezone
 * @returns Check task form data
 */
export function transformCheckTaskViewDataToFormData(
  viewData: CheckTaskViewData,
  warehouseTimezone: string
): CheckTaskFormData {
  const relatedEntity = viewData.relatedEntity || viewData.subject || '';
  const relatedId = viewData.relatedId ?? null;
  const baseData = extractBaseFormDataFromViewData(viewData, warehouseTimezone);

  return {
    ...baseData,
    type: 'check',
    checklist: viewData.checklist,
    subject: viewData.subject,
    category: viewData.category,
    spot: relatedEntity === YardTaskCheckEntity.Spot ? relatedId : null,
    area: relatedEntity === YardTaskCheckEntity.SpotArea ? relatedId : null,
    asset: relatedEntity === YardTaskCheckEntity.AssetContainer ? relatedId : null,
    inventoryGroups:
      relatedEntity === YardTaskCheckEntity.Warehouse ? (viewData.inventoryGroups ?? []) : undefined
  };
}

/**
 * Transforms move task view data to form data format (for forms)
 * @param viewData - View data
 * @param warehouseTimezone - Warehouse timezone
 * @returns Move task form data
 */
export function transformMoveTaskViewDataToFormData(
  viewData: MoveTaskViewData,
  warehouseTimezone: string
): MoveTaskFormData {
  const baseData = extractBaseFormDataFromViewData(viewData, warehouseTimezone);

  return {
    ...baseData,
    type: 'move',
    asset: viewData.assetContainer?.id || null,
    originSpot: viewData.originSpot?.id || null,
    destinationSpot: viewData.destinationSpot?.id || null,
    // Same reasoning as the read above: stays faithful to view data, undefined included.
    reserveDestinationSpot: viewData.reserveDestinationSpot
  };
}

/**
 * Transforms task view data to form data format (for forms)
 * @param viewData - View data
 * @returns Form data object (CheckTaskFormData or MoveTaskFormData)
 */
export function transformTaskViewDataToFormData(
  viewData: Maybe<TaskViewData>,
  warehouseTimezone: string
): Nullable<CheckTaskFormData | MoveTaskFormData> {
  if (!viewData) {
    return null;
  }

  if (isCheckTask(viewData)) {
    return transformCheckTaskViewDataToFormData(viewData, warehouseTimezone);
  }

  if (isMoveTask(viewData)) {
    return transformMoveTaskViewDataToFormData(viewData, warehouseTimezone);
  }

  return null;
}

/**
 * Transforms check task API response to form data format (for create/edit forms)
 * @param apiResponse - API response data
 * @param warehouseTimezone - Warehouse timezone
 * @returns Check task form data
 */
export function transformCheckTaskApiResponseToFormData(
  apiResponse: CheckTaskApiResponseData,
  warehouseTimezone: string
): CheckTaskFormData {
  const viewData = transformCheckTaskApiResponseToViewData(apiResponse);
  return transformCheckTaskViewDataToFormData(viewData, warehouseTimezone);
}

/**
 * Transforms move task API response to form data format (for create/edit forms)
 * @param apiResponse - API response data
 * @param warehouseTimezone - Warehouse timezone
 * @returns Move task form data
 */
export function transformMoveTaskApiResponseToFormData(
  apiResponse: MoveTaskApiResponseData,
  warehouseTimezone: string
): MoveTaskFormData {
  const viewData = transformMoveTaskApiResponseToViewData(apiResponse);
  return transformMoveTaskViewDataToFormData(viewData, warehouseTimezone);
}

/**
 * Transforms task API response to form data format (for create/edit forms)
 * @param apiResponse - API response data
 * @param warehouseTimezone - Warehouse timezone
 * @returns Form data object (CheckTaskFormData or MoveTaskFormData)
 */
export function transformTaskApiResponseToFormData(
  apiResponse: Maybe<TaskApiResponseData>,
  warehouseTimezone: string
): Nullable<CheckTaskFormData | MoveTaskFormData> {
  if (!apiResponse) {
    return null;
  }

  if (isCheckTask(apiResponse)) {
    return transformCheckTaskApiResponseToFormData(apiResponse, warehouseTimezone);
  }

  if (isMoveTask(apiResponse)) {
    return transformMoveTaskApiResponseToFormData(apiResponse, warehouseTimezone);
  }

  return null;
}

/**
 * Transforms base/common task form data to API request payload format
 * Only includes properties that were provided in the form data
 * @param taskData - Form task data (check or move, partial)
 * @param options - Options containing warehouseId (optional) and warehouseTimezone
 * @returns Partial API request payload with only common properties
 */
export function transformBaseTaskFormDataToApiRequestData(
  taskData: Partial<CheckTaskFormData> | Partial<MoveTaskFormData>,
  options?: { warehouseId?: string; warehouseTimezone?: string }
): Partial<CheckTaskApiRequestData> | Partial<MoveTaskApiRequestData> {
  if (!taskData) {
    throw new Error('No task data available');
  }

  const { warehouseId, warehouseTimezone } = options || {};
  const payload: Partial<CheckTaskApiRequestData> | Partial<MoveTaskApiRequestData> = {};

  if (taskData.assignee !== undefined) {
    payload.assignedTo = taskData.assignee || null;
  }

  if (taskData.description !== undefined) {
    payload.description = taskData.description || '';
  }

  if (taskData.tags !== undefined) {
    payload.tags = mapTags(taskData.tags);
  }

  const hasDueByDate = 'dueByDate' in taskData;
  const hasDueByTime = 'dueByTime' in taskData;

  if (hasDueByDate || hasDueByTime) {
    payload.dueDateTime = mapDueDateTime(taskData.dueByDate, taskData.dueByTime, warehouseTimezone);
  }

  if (warehouseId) {
    payload.warehouseId = warehouseId;
  }

  if (taskData.type) {
    payload.type = taskData.type;
  }

  if (taskData.status) {
    payload.status = taskData.status;
  }

  return payload;
}

type CheckTaskChildFlags = {
  hasChecklist: boolean;
  hasCategory: boolean;
  hasSpot: boolean;
  hasArea: boolean;
  hasAsset: boolean;
  hasInventoryGroups: boolean;
};

/**
 * Builds the `child` portion of a check task API request payload from form data.
 * Split out of {@link transformCheckTaskFormDataToApiRequestData} to keep that
 * function's cyclomatic complexity within lint limits.
 */
function buildCheckTaskChildPayload(
  taskData: Partial<CheckTaskFormData>,
  options: { warehouseId?: string; warehouseTimezone?: string } | undefined,
  flags: CheckTaskChildFlags
): Partial<CheckTaskApiRequestData['child']> {
  const { hasChecklist, hasCategory, hasSpot, hasArea, hasAsset, hasInventoryGroups } = flags;

  const checklist = hasChecklist ? mapChecklist(taskData.checklist) : [];
  const { relatedEntity, relatedId } = mapRelatedEntity(
    taskData.subject || '',
    hasSpot ? taskData.spot : undefined,
    hasArea ? taskData.area : undefined,
    hasAsset ? taskData.asset : undefined,
    options?.warehouseId
  );

  const child: Partial<CheckTaskApiRequestData['child']> = {};

  if (hasChecklist) {
    child.checklist = checklist;
  }

  // Inventory groups only apply to warehouse-scoped checks; never emit them for
  // asset/spot/area subjects even if the form left an (empty) array behind.
  if (hasInventoryGroups && relatedEntity === YardTaskCheckEntity.Warehouse) {
    child.inventoryGroups = mapInventoryGroups(taskData.inventoryGroups);
  }

  if (hasCategory && taskData.category !== undefined && taskData.category !== null) {
    const trimmed =
      typeof taskData.category === 'string' ? taskData.category.trim() : taskData.category;
    if (typeof trimmed === 'string' && trimmed.length > 0) {
      child.category = trimmed;
    }
  }

  // Only set both relatedEntity and relatedId if both are present (they must be paired)
  if (relatedEntity && relatedId !== null) {
    child.relatedEntity = relatedEntity as YardTaskCheckEntity;
    child.relatedId = relatedId;
  }

  return child;
}

/**
 * Transforms check task form data to API request payload format
 * @param taskData - Form task data
 * @param options - Options containing warehouseId (optional) and warehouseTimezone
 * @returns API request payload (partial, only includes provided properties)
 */
export function transformCheckTaskFormDataToApiRequestData(
  taskData: Partial<CheckTaskFormData>,
  options?: { warehouseId?: string; warehouseTimezone?: string }
): Partial<CheckTaskApiRequestData> {
  if (!taskData) {
    throw new Error('No task data available');
  }

  const payload = transformBaseTaskFormDataToApiRequestData(
    taskData,
    options
  ) as Partial<CheckTaskApiRequestData>;

  const flags: CheckTaskChildFlags = {
    hasChecklist: taskData.checklist !== undefined,
    hasCategory: taskData.category !== undefined,
    hasSpot: taskData.spot !== undefined,
    hasArea: taskData.area !== undefined,
    hasAsset: taskData.asset !== undefined,
    hasInventoryGroups: taskData.inventoryGroups !== undefined
  };
  const hasSubject = taskData.subject !== undefined;

  if (hasSubject || Object.values(flags).some(Boolean)) {
    const child = buildCheckTaskChildPayload(taskData, options, flags);

    if (!isEmpty(child)) {
      payload.child = child as CheckTaskApiRequestData['child'];
    }
  }

  return payload;
}

/**
 * Transforms move task form data to API request payload format
 * @param taskData - Form task data
 * @param options - Additional options
 * @returns API request payload (partial, only includes provided properties)
 */
export function transformMoveTaskFormDataToApiRequestData(
  taskData: Partial<MoveTaskFormData>,
  options?: { warehouseId?: string; warehouseTimezone?: string }
): Partial<MoveTaskApiRequestData> {
  if (!taskData) {
    throw new Error('No task data available');
  }

  const payload = transformBaseTaskFormDataToApiRequestData(
    taskData,
    options
  ) as Partial<MoveTaskApiRequestData>;

  const hasAsset = taskData.asset !== undefined;
  const hasOriginSpot = taskData.originSpot !== undefined;
  const hasDestinationSpot = taskData.destinationSpot !== undefined;
  // Omitting it is meaningful: the API then leaves the task's stored choice alone, which is how
  // Circe's move tasks reserve their destination without ever knowing about the field. `null` has
  // to take the same branch as `undefined` here — unlike the three fields above, this one has no
  // "clear it" meaning, so a null must never reach `Boolean(...)` and turn into an explicit false.
  const hasReserveDestinationSpot = !isNil(taskData.reserveDestinationSpot);

  if (hasAsset || hasOriginSpot || hasDestinationSpot || hasReserveDestinationSpot) {
    const child: Partial<MoveTaskApiRequestData['child']> = {};

    if (hasAsset) {
      child.assetContainerId = taskData.asset ?? null;
    }

    if (hasOriginSpot) {
      child.originSpotId = taskData.originSpot ?? null;
    }

    if (hasDestinationSpot) {
      child.destinationSpotId = taskData.destinationSpot ?? null;
    }

    if (hasReserveDestinationSpot) {
      child.reserveDestinationSpot = Boolean(taskData.reserveDestinationSpot);
    }

    if (!isEmpty(child)) {
      payload.child = child as MoveTaskApiRequestData['child'];
    }
  }

  return payload;
}

/**
 * Transforms form data to API request payload format
 * @param taskData - Form task data (check or move)
 * @param options - Additional options
 * @returns API request payload (partial) or null if invalid
 */
export function transformFormDataToApiRequestData(
  taskData?: Maybe<Partial<CheckTaskFormData> | Partial<MoveTaskFormData>>,
  options?: { warehouseId?: string; warehouseTimezone?: string }
): Nullable<Partial<CheckTaskApiRequestData> | Partial<MoveTaskApiRequestData>> {
  if (!taskData) {
    return null;
  }

  if (isCheckTask(taskData)) {
    return transformCheckTaskFormDataToApiRequestData(taskData, options);
  }

  if (isMoveTask(taskData)) {
    return transformMoveTaskFormDataToApiRequestData(taskData, options);
  }

  // If we can't detect the task type, use base transformation (for partial updates like tags only)
  return transformBaseTaskFormDataToApiRequestData(taskData, options);
}
